import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  AuthResponse,
  LoginRequest,
  RefreshTokenRequest,
  SelectAccountRequest,
  SwitchAccountRequest,
  VerifyOtpRequest,
  SetPasswordRequest,
  ImpersonateRequest,
  ImpersonationHandoffResponse,
  AccountMembershipResponse
} from './types';

// Auth API functions

export function login(data: LoginRequest) {
  return apiClient.post<AuthResponse>('/auth/login', data);
}

export function refreshToken(data: RefreshTokenRequest) {
  return apiClient.post<AuthResponse>('/auth/refresh', data);
}

export function selectAccount(data: SelectAccountRequest) {
  return apiClient.post<AuthResponse>('/auth/select-account', data);
}

// VEK-144 (ACCT-02) — the account a target link names; 403 for a non-member, unknown or inactive account.
export function targetAccount(data: SelectAccountRequest) {
  return apiClient.post<AuthResponse>('/auth/target-account', data);
}

export function switchAccount(data: SwitchAccountRequest) {
  return apiClient.post<AuthResponse>('/auth/switch-account', data);
}

export function getAccounts() {
  return apiClient.get<AccountMembershipResponse[]>('/auth/accounts');
}

export function verifyOtp(data: VerifyOtpRequest) {
  return apiClient.post<AuthResponse>('/auth/verify-otp', data);
}

export function setPassword(data: SetPasswordRequest) {
  return apiClient.post<AuthResponse>('/auth/set-password', data);
}

export function impersonate(data: ImpersonateRequest) {
  return apiClient.post<AuthResponse>('/auth/impersonate', data);
}

/** Issues a one-time code (admin-authorized) to start an impersonation session on another device. */
export function generateImpersonationQr(data: ImpersonateRequest) {
  return apiClient.post<ImpersonationHandoffResponse>('/auth/impersonate/qr', data);
}

/** Exchanges a scanned one-time code for a short-lived impersonation session (no auth required). */
export function exchangeImpersonationQr(code: string) {
  return apiClient.post<AuthResponse>('/public/auth/impersonate/qr/exchange', { code });
}

export function logout(refreshTokenValue?: string | null) {
  return apiClient.post<void>('/auth/logout', {
    refreshToken: refreshTokenValue || undefined
  });
}

// Auth hooks

export function useLogin() {
  return useMutation({
    mutationFn: (data: LoginRequest) => login(data)
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: (data: RefreshTokenRequest) => refreshToken(data)
  });
}

export function useSelectAccount() {
  return useMutation({
    mutationFn: (data: SelectAccountRequest) => selectAccount(data)
  });
}

export function useSwitchAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SwitchAccountRequest) => switchAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries();
    }
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: ['auth', 'accounts'],
    queryFn: () => getAccounts()
  });
}

export function useVerifyOtp() {
  return useMutation({
    mutationFn: (data: VerifyOtpRequest) => verifyOtp(data)
  });
}

export function useSetPassword() {
  return useMutation({
    mutationFn: (data: SetPasswordRequest) => setPassword(data)
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (refreshTokenValue?: string | null) => logout(refreshTokenValue),
    onSuccess: () => {
      queryClient.clear();
    }
  });
}
